import * as dompurifyModule from 'dompurify';

/**
 * Sanitiser for rich-text message bodies.
 *
 * Messages are authored as HTML by a Branch Admin and rendered into every
 * recipient's browser, including thousands of clients. That is a stored-XSS
 * path, so the HTML is passed through a deny-by-default allowlist before it
 * ever reaches dangerouslySetInnerHTML.
 *
 * The allowlist covers exactly what the Quill toolbar can produce - formatting,
 * lists, headings, links, and inline colour/font styling - and nothing else.
 * No script, iframe, object, embed, form or event handler survives.
 */

// Tags Quill emits, plus the inline wrappers it uses for colour and font.
const ALLOWED_TAGS = [
  'p',
  'br',
  'span',
  'div',
  'strong',
  'b',
  'em',
  'i',
  'u',
  's',
  'strike',
  'sub',
  'sup',
  'blockquote',
  'pre',
  'code',
  'ol',
  'ul',
  'li',
  'h1',
  'h2',
  'h3',
  'h4',
  'a',
];

// `class` carries Quill's alignment/size/indent classes (ql-align-center etc.);
// `style` carries the colour and font pickers. Both are scrubbed further below.
const ALLOWED_ATTR = ['href', 'target', 'rel', 'class', 'style'];

// Only presentational CSS survives. Anything that can position, load a
// resource, or escape the container (position, background-image, url(...)) is
// dropped rather than trusted.
const ALLOWED_CSS_PROPS = [
  'color',
  'background-color',
  'font-family',
  'font-size',
  'font-weight',
  'font-style',
  'text-align',
  'text-decoration',
];

const SAFE_LINK = /^(https?:|mailto:|tel:)/i;

let purifier: any = null;

/**
 * dompurify self-binds to `window` in a browser but exports a bare factory
 * when there is no global window at import time (jest/node). Handle both, and
 * install the hooks exactly once against whichever instance we end up with.
 */
const getPurifier = () => {
  if (purifier) return purifier;

  const mod: any = dompurifyModule;
  purifier =
    typeof mod.sanitize === 'function'
      ? mod
      : mod(typeof window !== 'undefined' ? window : undefined);

  if (!purifier || typeof purifier.sanitize !== 'function') {
    purifier = null;
    return null;
  }

  purifier.addHook('afterSanitizeAttributes', (node: any) => {
    if (!node || node.nodeType !== 1) return;

    if (node.hasAttribute && node.hasAttribute('href')) {
      const href = (node.getAttribute('href') || '').trim();
      if (!SAFE_LINK.test(href)) {
        node.removeAttribute('href');
      } else {
        // Message links open away from the portal; never hand the opener over.
        node.setAttribute('target', '_blank');
        node.setAttribute('rel', 'noopener noreferrer');
      }
    }

    if (node.hasAttribute && node.hasAttribute('style')) {
      const kept: string[] = [];
      const declarations = (
        node.getAttribute('style') || ''
      ).split(';');

      declarations.forEach((declaration: string) => {
        const colon = declaration.indexOf(':');
        if (colon === -1) return;
        const prop = declaration
          .slice(0, colon)
          .trim()
          .toLowerCase();
        const value = declaration.slice(colon + 1).trim();
        if (!value) return;
        if (ALLOWED_CSS_PROPS.indexOf(prop) === -1) return;
        // Belt and braces: no url()/expression() smuggled through a colour.
        if (
          /url\s*\(|expression\s*\(|javascript:/i.test(value)
        ) {
          return;
        }
        kept.push(`${prop}: ${value}`);
      });

      if (kept.length) {
        node.setAttribute('style', kept.join('; '));
      } else {
        node.removeAttribute('style');
      }
    }
  });

  return purifier;
};

/** Returns HTML safe to hand to dangerouslySetInnerHTML. */
export const sanitizeHtml = (html?: string): string => {
  if (!html) return '';

  const purify = getPurifier();
  // No sanitiser means no safe way to render markup - fall back to escaping
  // everything rather than trusting the input.
  if (!purify) {
    return html
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  return purify.sanitize(html, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    FORBID_TAGS: [
      'script',
      'style',
      'iframe',
      'object',
      'embed',
      'form',
      'input',
      'button',
      'link',
      'meta',
      'base',
    ],
    ALLOW_DATA_ATTR: false,
    USE_PROFILES: { html: true },
  });
};

/**
 * Readable one-line text from a rich-text body.
 *
 * Used wherever markup would be noise rather than value: the header banner's
 * single-line preview and the admin/employee message tables, which would
 * otherwise print raw <p> tags.
 */
export const htmlToPlainText = (html?: string): string => {
  if (!html) return '';

  // Sanitise first so the throwaway element can never run anything, then read
  // textContent. The element is never attached to the document.
  const clean = sanitizeHtml(html);
  const holder = document.createElement('div');
  holder.innerHTML = clean;

  return (holder.textContent || '')
    .replace(/\s+/g, ' ')
    .trim();
};

export default sanitizeHtml;
